import React, { useState } from 'react';

/**
 * Verlofwijzer — Interactieve beslisboom voor medisch/bijzonder verlof
 * 
 * Gebaseerd op:
 * - Wet arbeid en zorg (WAZO)
 * - Retail / Wonen cao toepassing
 * 
 * Bepaalt of verlofuren afgetrokken moeten worden of dat het
 * onder kort verzuim / calamiteitenverlof valt.
 */

const STAPPEN = {
  start: {
    vraag: 'Waar gaat de afwezigheid over?',
    opties: [
      { key: 'eigen', label: '🩺 Medische afspraak voor mijzelf', icon: '🩺' },
      { key: 'kind', label: '👶 Mee naar arts met kind', icon: '👶' },
      { key: 'partner', label: '💑 Mee naar arts met partner', icon: '💑' },
      { key: 'anders', label: '📋 Iets anders (bijzonder verlof)', icon: '📋' },
    ],
  },

  // === EIGEN MEDISCHE AFSPRAAK ===
  eigen_noodzaak: {
    vraag: 'Is de afspraak medisch noodzakelijk?',
    toelichting: 'Denk aan: specialist, ziekenhuis, verplichte keuring, zwangerschapscontrole',
    opties: [
      { key: 'ja', label: '✅ Ja, medisch noodzakelijk' },
      { key: 'nee', label: '❌ Nee, routine/planbaar' },
    ],
  },
  eigen_werktijd: {
    vraag: 'Kan de afspraak redelijkerwijs buiten werktijd?',
    toelichting: 'Zijn er avond- of weekendopties? Heeft de praktijk ruimere openingstijden?',
    opties: [
      { key: 'nee', label: '❌ Nee, alleen tijdens werktijd mogelijk' },
      { key: 'ja', label: '✅ Ja, maar ik kies voor werktijd' },
    ],
  },

  // === KIND ===
  kind_leeftijd: {
    vraag: 'Is het kind minderjarig (onder 18)?',
    opties: [
      { key: 'ja', label: '✅ Ja, minderjarig' },
      { key: 'nee', label: '❌ Nee, 18 jaar of ouder' },
    ],
  },
  kind_noodzaak: {
    vraag: 'Is het bezoek medisch noodzakelijk?',
    toelichting: 'Ziekenhuis, specialist, spoed huisarts, consult waar ouder bij moet zijn',
    opties: [
      { key: 'ja', label: '✅ Ja, medisch noodzakelijk' },
      { key: 'nee', label: '❌ Nee, routine controle' },
    ],
  },
  kind_aanwezigheid: {
    vraag: 'Is jouw aanwezigheid als ouder vereist?',
    toelichting: 'Kan de andere ouder/verzorger ook meegaan?',
    opties: [
      { key: 'ja', label: '✅ Ja, ik ben de enige die kan' },
      { key: 'nee', label: '❌ Nee, andere ouder/verzorger kan ook' },
    ],
  },

  // === PARTNER ===
  partner_ernst: {
    vraag: 'Is je partner ernstig ziek of is er acute zorg nodig?',
    toelichting: 'Ziekenhuisopname, ernstige diagnose, acute situatie',
    opties: [
      { key: 'ja', label: '✅ Ja, ernstige situatie' },
      { key: 'nee', label: '❌ Nee, routine afspraak' },
    ],
  },
  partner_enige: {
    vraag: 'Ben jij de enige die kan begeleiden?',
    toelichting: 'Kan je partner zelfstandig gaan? Is er een ander familielid?',
    opties: [
      { key: 'ja', label: '✅ Ja, ik ben de enige' },
      { key: 'nee', label: '❌ Nee, partner kan zelf of ander familielid' },
    ],
  },
};

// Decision tree flow
function volgendeStap(huidigeStap, antwoord, antwoorden) {
  switch (huidigeStap) {
    case 'start':
      if (antwoord === 'eigen') return 'eigen_noodzaak';
      if (antwoord === 'kind') return 'kind_leeftijd';
      if (antwoord === 'partner') return 'partner_ernst';
      return null; // anders → bijzonder verlof, geen verdere vragen
    case 'eigen_noodzaak':
      if (antwoord === 'ja') return 'eigen_werktijd';
      return null; // nee → verlof
    case 'eigen_werktijd':
      return null; // eindpunt
    case 'kind_leeftijd':
      if (antwoord === 'ja') return 'kind_noodzaak';
      return null; // volwassen kind → verlof
    case 'kind_noodzaak':
      if (antwoord === 'ja') return 'kind_aanwezigheid';
      return null; // routine → verlof
    case 'kind_aanwezigheid':
      return null; // eindpunt
    case 'partner_ernst':
      if (antwoord === 'ja') return 'partner_enige';
      return null; // routine → verlof
    case 'partner_enige':
      return null; // eindpunt
    default:
      return null;
  }
}

// Determine result based on all answers
function bepaalResultaat(antwoorden) {
  const { start, eigen_noodzaak, eigen_werktijd, kind_leeftijd, kind_noodzaak, kind_aanwezigheid, partner_ernst, partner_enige } = antwoorden;

  if (start === 'anders') {
    return {
      type: 'bijzonder',
      aftrek: true,
      titel: 'Bijzonder verlof',
      kleur: '#8764B8',
      uitleg: 'Dit valt onder bijzonder verlof. De uren worden van je verlofsaldo afgetrokken.',
      wettelijk: null,
    };
  }

  // EIGEN MEDISCH
  if (start === 'eigen') {
    if (eigen_noodzaak === 'nee') {
      return {
        type: 'verlof',
        aftrek: true,
        titel: 'Verlofuren — planbare afspraak',
        kleur: '#E86E1C',
        uitleg: 'Een routine/planbare afspraak mag je van verlofuren afboeken.',
        wettelijk: 'De afspraak is niet medisch noodzakelijk en kan mogelijk buiten werktijd.',
        tip: '💡 Tip: plan deze afspraken buiten werktijd waar mogelijk.',
      };
    }
    if (eigen_werktijd === 'ja') {
      return {
        type: 'verlof',
        aftrek: true,
        titel: 'Verlofuren — buiten werktijd mogelijk',
        kleur: '#E86E1C',
        uitleg: 'Hoewel medisch noodzakelijk, kon de afspraak buiten werktijd. Daarom gelden verlofuren.',
        wettelijk: 'Art. 4:1 WAZO: kort verzuim geldt alleen als het niet buiten werktijd kan.',
      };
    }
    if (eigen_werktijd === 'nee') {
      return {
        type: 'kort-verzuim',
        aftrek: false,
        titel: '✅ Verlof medische — geen aftrek',
        kleur: '#107C10',
        uitleg: 'Dit is medisch noodzakelijk en kan niet buiten werktijd. Dit valt onder Verlof medische (calamiteitenverlof).',
        wettelijk: 'Art. 4:1 WAZO: recht op kort verzuimverlof met loondoorbetaling bij medische noodzaak die niet buiten werktijd kan.',
        voorbeelden: 'Specialist, ziekenhuis, spoedafspraak, verplichte keuring, zwangerschapscontrole.',
      };
    }
  }

  // KIND
  if (start === 'kind') {
    if (kind_leeftijd === 'nee') {
      return {
        type: 'verlof',
        aftrek: true,
        titel: 'Verlofuren — volwassen kind',
        kleur: '#E86E1C',
        uitleg: 'Bij een meerderjarig kind (18+) is er geen automatisch recht op kort verzuimverlof voor begeleiding.',
        wettelijk: 'WAZO-recht op kort verzuim geldt primair voor minderjarige kinderen.',
      };
    }
    if (kind_noodzaak === 'nee') {
      return {
        type: 'verlof',
        aftrek: true,
        titel: 'Verlofuren — routine controle',
        kleur: '#E86E1C',
        uitleg: 'Een routine controle van je kind is planbaar. Daarvoor gelden verlofuren.',
        tip: '💡 Tip: plan routine controles waar mogelijk buiten werktijd.',
      };
    }
    if (kind_aanwezigheid === 'nee') {
      return {
        type: 'verlof',
        aftrek: true,
        titel: 'Verlofuren — andere verzorger beschikbaar',
        kleur: '#E86E1C',
        uitleg: 'Er is een andere ouder/verzorger die mee kan. Jouw aanwezigheid is niet strikt noodzakelijk.',
        wettelijk: 'Bij beschikbaarheid van een andere verzorger kan de werkgever verlofuren verlangen.',
      };
    }
    if (kind_aanwezigheid === 'ja') {
      return {
        type: 'kort-verzuim',
        aftrek: false,
        titel: '✅ Verlof medische — geen aftrek',
        kleur: '#107C10',
        uitleg: 'Minderjarig kind, medisch noodzakelijk bezoek, en jij bent de enige die kan begeleiden. Dit valt onder Verlof medische (calamiteitenverlof).',
        wettelijk: 'Art. 4:1 WAZO: calamiteitenverlof voor noodzakelijke verzorging van een ziek kind waarbij aanwezigheid ouder vereist is.',
        voorbeelden: 'Ziekenhuisbezoek, spoed huisarts, specialist consult.',
      };
    }
  }

  // PARTNER
  if (start === 'partner') {
    if (partner_ernst === 'nee') {
      return {
        type: 'verlof',
        aftrek: true,
        titel: 'Verlofuren — routine afspraak partner',
        kleur: '#E86E1C',
        uitleg: 'Bij een routine afspraak van je partner is er geen recht op kort verzuim. Partner kan zelfstandig gaan.',
        wettelijk: 'Er is geen automatisch recht op verlof voor begeleiding bij niet-ernstige afspraken van een partner.',
      };
    }
    if (partner_enige === 'nee') {
      return {
        type: 'verlof',
        aftrek: true,
        titel: 'Verlofuren — partner kan zelf of ander familielid',
        kleur: '#E86E1C',
        uitleg: 'Hoewel de situatie ernstig is, kan de partner zelf gaan of een ander familielid begeleiden.',
        wettelijk: 'Kort verzuimverlof geldt bij partner alleen als de medewerker de enige mogelijke begeleider is.',
      };
    }
    if (partner_enige === 'ja') {
      return {
        type: 'kort-verzuim',
        aftrek: false,
        titel: '✅ Verlof medische — geen aftrek',
        kleur: '#107C10',
        uitleg: 'Partner ernstig ziek en jij bent de enige begeleider. Dit valt onder Verlof medische (kort verzuim of kortdurend zorgverlof).',
        wettelijk: 'Art. 4:1 WAZO (calamiteitenverlof) of Art. 5:1 WAZO (kortdurend zorgverlof) bij noodzakelijke verzorging van ernstig zieke partner.',
      };
    }
  }

  // Fallback
  return {
    type: 'bijzonder',
    aftrek: true,
    titel: 'Bijzonder verlof',
    kleur: '#8764B8',
    uitleg: 'Op basis van je antwoorden valt dit onder bijzonder verlof.',
  };
}

function Verlofwijzer({ onResultaat, onSluit }) {
  const [antwoorden, setAntwoorden] = useState({});
  const [huidigeStap, setHuidigeStap] = useState('start');
  const [resultaat, setResultaat] = useState(null);
  const [stapHistory, setStapHistory] = useState(['start']);

  const beantwoord = (antwoord) => {
    const nieuweAntwoorden = { ...antwoorden, [huidigeStap]: antwoord };
    setAntwoorden(nieuweAntwoorden);

    const volgende = volgendeStap(huidigeStap, antwoord, nieuweAntwoorden);
    if (volgende) {
      setHuidigeStap(volgende);
      setStapHistory(prev => [...prev, volgende]);
    } else {
      const res = bepaalResultaat(nieuweAntwoorden);
      setResultaat(res);
    }
  };

  const stapTerug = () => {
    if (stapHistory.length <= 1) return;
    const nieuweHistory = stapHistory.slice(0, -1);
    const vorigeStap = nieuweHistory[nieuweHistory.length - 1];
    const nieuweAntwoorden = { ...antwoorden };
    delete nieuweAntwoorden[huidigeStap];
    setAntwoorden(nieuweAntwoorden);
    setStapHistory(nieuweHistory);
    setHuidigeStap(vorigeStap);
    setResultaat(null);
  };

  const reset = () => {
    setAntwoorden({});
    setHuidigeStap('start');
    setResultaat(null);
    setStapHistory(['start']);
  };

  const stap = STAPPEN[huidigeStap];
  const voortgang = resultaat ? 100 : Math.round((stapHistory.length / 4) * 100);

  return (
    <div style={sty.container}>
      <div style={sty.header}>
        <div style={sty.headerLeft}>
          <span style={sty.headerIcon}>⚖️</span>
          <span style={sty.headerTitle}>Verlofwijzer</span>
        </div>
        <button onClick={onSluit} style={sty.sluitBtn}>✕</button>
      </div>

      <div style={sty.subtitel}>
        Beantwoord de vragen om te bepalen of er verlofuren worden afgetrokken.
        <span style={sty.wettelijk}>Gebaseerd op WAZO en Retail/Wonen cao.</span>
      </div>

      {/* Voortgangsbalk */}
      <div style={sty.voortgangContainer}>
        <div style={{ ...sty.voortgangBalk, width: `${voortgang}%` }} />
      </div>

      {/* Eerdere antwoorden als breadcrumbs */}
      {stapHistory.length > 1 && !resultaat && (
        <div style={sty.breadcrumbs}>
          {stapHistory.slice(0, -1).map((s, i) => {
            const stapInfo = STAPPEN[s];
            const antwoord = antwoorden[s];
            const optie = stapInfo?.opties?.find(o => o.key === antwoord);
            return (
              <div key={i} style={sty.breadcrumb}>
                <span style={sty.breadcrumbQ}>{stapInfo?.vraag}</span>
                <span style={sty.breadcrumbA}>{optie?.label || antwoord}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Huidige vraag */}
      {!resultaat && stap && (
        <div style={sty.vraagContainer}>
          <div style={sty.vraagTekst}>{stap.vraag}</div>
          {stap.toelichting && (
            <div style={sty.toelichting}>{stap.toelichting}</div>
          )}
          <div style={sty.opties}>
            {stap.opties.map((optie) => (
              <button key={optie.key} style={sty.optieBtn}
                onClick={() => beantwoord(optie.key)}>
                {optie.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Resultaat */}
      {resultaat && (
        <div style={{ ...sty.resultaat, borderColor: resultaat.kleur, background: resultaat.aftrek ? '#fff8f0' : '#f0fff0' }}>
          <div style={{ ...sty.resultaatTitel, color: resultaat.kleur }}>
            {resultaat.titel}
          </div>
          <div style={sty.resultaatUitleg}>{resultaat.uitleg}</div>

          {resultaat.wettelijk && (
            <div style={sty.resultaatWettelijk}>
              <strong>📜 Wettelijke basis:</strong> {resultaat.wettelijk}
            </div>
          )}

          {resultaat.voorbeelden && (
            <div style={sty.resultaatVoorbeelden}>
              <strong>Voorbeelden:</strong> {resultaat.voorbeelden}
            </div>
          )}

          {resultaat.tip && (
            <div style={sty.resultaatTip}>{resultaat.tip}</div>
          )}

          {resultaat.aftrek ? (
            <div style={sty.aftrekIndicator}>
              <span style={{ fontSize: 18 }}>📊</span>
              <span>Verlofuren worden <strong>wel</strong> van je saldo afgetrokken</span>
            </div>
          ) : (
            <div style={sty.geenAftrekIndicator}>
              <span style={{ fontSize: 18 }}>✅</span>
              <span>Geen verlofuren aftrek — loon wordt doorbetaald</span>
            </div>
          )}

          <div style={sty.resultaatActies}>
            <button style={sty.toepassenBtn} onClick={() => {
              onResultaat(resultaat);
              onSluit();
            }}>
              {resultaat.aftrek
                ? `📋 Indienen als ${resultaat.type === 'verlof' ? 'verlof' : 'bijzonder verlof'}`
                : '✅ Indienen als Verlof medische (geen aftrek)'}
            </button>
            <button style={sty.opnieuwBtn} onClick={reset}>🔄 Opnieuw</button>
          </div>

          {/* Werkgever rechten */}
          <div style={sty.werkgeverInfo}>
            <div style={sty.werkgeverTitel}>ℹ️ De werkgever mag:</div>
            <div style={sty.werkgeverItem}>• Redelijkheid toetsen</div>
            <div style={sty.werkgeverItem}>• Bewijs vragen (afspraakbevestiging)</div>
            <div style={sty.werkgeverItem}>• Vragen of het buiten werktijd kan</div>
            <div style={sty.werkgeverTitel2}>De werkgever mag niet:</div>
            <div style={sty.werkgeverItem}>• Structureel alle doktersbezoeken als verlof afboeken</div>
            <div style={sty.werkgeverItem}>• Wettelijk Verlof medische (kort verzuimverlof) weigeren</div>
          </div>
        </div>
      )}

      {/* Navigatie */}
      {!resultaat && stapHistory.length > 1 && (
        <button style={sty.terugBtn} onClick={stapTerug}>← Vorige vraag</button>
      )}
    </div>
  );
}

const sty = {
  container: {
    background: '#fff', border: '2px solid #8764B8', borderRadius: 12,
    padding: 0, marginBottom: 20, overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', background: '#8764B8', color: '#fff',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  headerIcon: { fontSize: 20 },
  headerTitle: { fontSize: 15, fontWeight: 700 },
  sluitBtn: {
    background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
    borderRadius: '50%', width: 28, height: 28, fontSize: 14,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  subtitel: {
    padding: '10px 16px 4px', fontSize: 12, color: '#605e5c', lineHeight: 1.5,
  },
  wettelijk: {
    display: 'block', fontSize: 10, color: '#a19f9d', marginTop: 2, fontStyle: 'italic',
  },
  voortgangContainer: {
    height: 3, background: '#edebe9', margin: '8px 16px',
    borderRadius: 2, overflow: 'hidden',
  },
  voortgangBalk: {
    height: '100%', background: '#8764B8', borderRadius: 2,
    transition: 'width 0.3s ease',
  },
  breadcrumbs: {
    padding: '0 16px 8px', display: 'flex', flexDirection: 'column', gap: 4,
  },
  breadcrumb: {
    display: 'flex', flexDirection: 'column', gap: 1,
    padding: '6px 10px', background: '#faf9f8', borderRadius: 6,
    borderLeft: '3px solid #8764B8',
  },
  breadcrumbQ: { fontSize: 10, color: '#a19f9d' },
  breadcrumbA: { fontSize: 12, color: '#323130', fontWeight: 600 },
  vraagContainer: {
    padding: '12px 16px 16px',
  },
  vraagTekst: {
    fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 6,
  },
  toelichting: {
    fontSize: 12, color: '#605e5c', marginBottom: 12, lineHeight: 1.5,
    fontStyle: 'italic',
  },
  opties: {
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  optieBtn: {
    padding: '12px 16px', borderRadius: 10,
    border: '2px solid #edebe9', background: '#fff',
    fontSize: 13, fontWeight: 600, color: '#323130',
    cursor: 'pointer', textAlign: 'left',
    transition: 'all 0.15s',
  },
  resultaat: {
    margin: '0 16px 16px', padding: 16, borderRadius: 10,
    border: '2px solid', lineHeight: 1.6,
  },
  resultaatTitel: {
    fontSize: 16, fontWeight: 800, marginBottom: 8,
  },
  resultaatUitleg: {
    fontSize: 13, color: '#323130', marginBottom: 8,
  },
  resultaatWettelijk: {
    fontSize: 11, color: '#605e5c', background: '#f5f5f5',
    padding: '8px 10px', borderRadius: 6, marginBottom: 8, lineHeight: 1.5,
  },
  resultaatVoorbeelden: {
    fontSize: 11, color: '#605e5c', marginBottom: 8,
  },
  resultaatTip: {
    fontSize: 12, color: '#0078D4', background: '#f0f6ff',
    padding: '8px 10px', borderRadius: 6, marginBottom: 8,
  },
  aftrekIndicator: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 12px', background: '#fff4ee', border: '1px solid #E86E1C',
    borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#E86E1C', marginBottom: 12,
  },
  geenAftrekIndicator: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 12px', background: '#f0fff0', border: '1px solid #107C10',
    borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#107C10', marginBottom: 12,
  },
  resultaatActies: {
    display: 'flex', gap: 8, flexWrap: 'wrap',
  },
  toepassenBtn: {
    flex: 1, padding: '10px 16px', borderRadius: 8, border: 'none',
    background: '#8764B8', color: '#fff', fontSize: 13, fontWeight: 700,
    cursor: 'pointer', minWidth: 0,
  },
  opnieuwBtn: {
    padding: '10px 16px', borderRadius: 8, border: '1px solid #c8c6c4',
    background: '#fff', color: '#605e5c', fontSize: 13, fontWeight: 600,
    cursor: 'pointer',
  },
  werkgeverInfo: {
    marginTop: 12, padding: '10px 12px', background: '#faf9f8',
    borderRadius: 8, border: '1px solid #edebe9',
  },
  werkgeverTitel: { fontSize: 11, fontWeight: 700, color: '#323130', marginBottom: 4 },
  werkgeverTitel2: { fontSize: 11, fontWeight: 700, color: '#D13438', marginTop: 6, marginBottom: 4 },
  werkgeverItem: { fontSize: 11, color: '#605e5c', lineHeight: 1.6 },
  terugBtn: {
    display: 'block', margin: '0 16px 16px', padding: '8px 14px',
    borderRadius: 8, border: '1px solid #c8c6c4', background: '#fff',
    color: '#605e5c', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
};

export default Verlofwijzer;
